import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Input } from '#/shared/ui/input'
import { Tooltip } from '#/shared/ui/tooltip'

import { EditFooter } from './edit-footer'
import { SectionEditButton } from './section-edit-button'

export interface NotesValue {
  lbNote: string
  cmNote: string
}

/** Pill-row style used in the full detail page (`ShipmentCard`). */
function PillNoteRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-8 items-center px-2.5 py-1">
      <div className="flex min-h-6 flex-1 items-center gap-1.25 rounded-md bg-gray-100 px-1.25">
        <span className="shrink-0 text-xs text-gray-900">{label}</span>
        <span className="text-sm text-gray-900">{value}</span>
      </div>
    </div>
  )
}

function PillNotes({
  lbNote,
  cmNote,
  customerNoteOnly,
}: {
  lbNote: string
  cmNote: string
  customerNoteOnly?: boolean
}) {
  const { t } = useTranslation()
  return (
    <>
      {!customerNoteOnly && lbNote && (
        <PillNoteRow label={t('shipmentCard.lbNote')} value={lbNote} />
      )}
      {cmNote ? (
        <PillNoteRow label={t('shipmentCard.cmNote')} value={cmNote} />
      ) : (
        <div className="flex h-8 items-center px-2.5">
          <span className="px-1.25 text-sm text-gray-400">
            {t('shipmentCard.customerNotePlaceholder')}
          </span>
        </div>
      )}
    </>
  )
}

/** Compact inline style used in the sidebar (`LB:` / `CM:` gray labels). */
function InlineNote({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex h-6 min-w-0 flex-1 items-center gap-1.25">
      <span className="shrink-0 text-xs text-gray-400">{label}</span>
      <Tooltip content={value}>
        <span className="min-w-0 truncate text-sm text-gray-900">{value}</span>
      </Tooltip>
    </div>
  )
}

function InlineNotes({
  lbNote,
  cmNote,
  customerNoteOnly,
}: {
  lbNote: string
  cmNote: string
  customerNoteOnly?: boolean
}) {
  const { t } = useTranslation()
  const showLb = !customerNoteOnly && !!lbNote
  if (!showLb && !cmNote) {
    return (
      <div className="flex h-8 items-center">
        <span className="text-sm text-gray-400">
          {t('shipmentCard.customerNotePlaceholder')}
        </span>
      </div>
    )
  }
  return (
    <div className="flex min-h-8 items-center gap-2.5">
      {showLb && (
        <InlineNote label={t('shipmentCard.lbNoteShort')} value={lbNote} />
      )}
      {cmNote && (
        <InlineNote label={t('shipmentCard.cmNoteShort')} value={cmNote} />
      )}
    </div>
  )
}

function NotesView({
  lbNote,
  cmNote,
  onEdit,
  customerNoteOnly,
  flush,
}: {
  lbNote: string
  cmNote: string
  onEdit?: () => void
  customerNoteOnly?: boolean
  flush?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'group/notes relative flex min-h-8 flex-col rounded-md',
        // flush (sidebar): pad inside the parent's inset so the hover bg keeps
        // an even margin from the panel edges instead of bleeding to them.
        flush ? 'px-2.5' : 'mx-2.5',
        onEdit && 'hover:bg-gray-100',
      )}
    >
      {onEdit && (
        <SectionEditButton
          label={t('shipmentCard.editNotes')}
          onClick={onEdit}
          groupName="notes"
        />
      )}
      {flush ? (
        <InlineNotes
          lbNote={lbNote}
          cmNote={cmNote}
          customerNoteOnly={customerNoteOnly}
        />
      ) : (
        <PillNotes
          lbNote={lbNote}
          cmNote={cmNote}
          customerNoteOnly={customerNoteOnly}
        />
      )}
    </div>
  )
}

function NotesEdit({
  lbNote,
  cmNote,
  onCancel,
  onSave,
  customerNoteOnly,
  flush,
  editClassName,
  showTitle,
}: {
  lbNote: string
  cmNote: string
  onCancel: () => void
  onSave: (notes: NotesValue) => void | Promise<void>
  customerNoteOnly?: boolean
  flush?: boolean
  editClassName?: string
  showTitle?: boolean
}) {
  const { t } = useTranslation()
  const [lb, setLb] = useState(lbNote)
  const [cm, setCm] = useState(cmNote)
  const [submitting, setSubmitting] = useState(false)
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    try {
      await onSave({ lbNote: lb, cmNote: cm })
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex flex-col rounded-md',
        flush ? 'bg-transparent' : 'mx-2.5 bg-gray-100',
        editClassName,
      )}
    >
      {showTitle && (
        <div className="flex h-6 items-center px-2.5">
          <span className="text-xs uppercase text-gray-500">
            {t('shipmentCard.notes')}
          </span>
        </div>
      )}
      <div
        className={cn(
          'flex flex-col gap-2 px-2.5 pb-2.5',
          showTitle ? 'pt-1' : 'pt-2.5',
        )}
      >
        {!customerNoteOnly && (
          <Input
            inputSize="sm"
            labelSize="sm"
            clearable
            label={t('orderCreate.shipment.loadBoardNote')}
            value={lb}
            onChange={(e) => setLb(e.target.value)}
          />
        )}
        <Input
          inputSize="sm"
          labelSize="sm"
          clearable
          label={t('orderCreate.shipment.customerNote')}
          value={cm}
          onChange={(e) => setCm(e.target.value)}
        />
      </div>
      <EditFooter onCancel={onCancel} saveType="submit" disabled={submitting} />
    </form>
  )
}

interface NotesSectionProps {
  lbNote: string
  cmNote: string
  editing: boolean
  onEditStart: () => void
  onCancel: () => void
  onSave: (notes: NotesValue) => void | Promise<void>
  customerNoteOnly?: boolean
  readOnly?: boolean
  /** Drops the section's mx-2.5 insets for flush sidebar layouts. */
  flush?: boolean
  editClassName?: string
  /** Renders a "NOTES" header on the edit form (sidebar gray-card style). */
  showEditTitle?: boolean
}

export function NotesSection({
  lbNote,
  cmNote,
  editing,
  onEditStart,
  onCancel,
  onSave,
  customerNoteOnly,
  readOnly,
  flush,
  editClassName,
  showEditTitle,
}: NotesSectionProps) {
  if (readOnly && !lbNote && !cmNote) return null
  if (editing && !readOnly) {
    return (
      <NotesEdit
        lbNote={lbNote}
        cmNote={cmNote}
        onCancel={onCancel}
        onSave={onSave}
        customerNoteOnly={customerNoteOnly}
        flush={flush}
        editClassName={editClassName}
        showTitle={showEditTitle}
      />
    )
  }
  return (
    <NotesView
      lbNote={lbNote}
      cmNote={cmNote}
      onEdit={readOnly ? undefined : onEditStart}
      customerNoteOnly={customerNoteOnly}
      flush={flush}
    />
  )
}
